import React, { useState } from "react";
import { Building, CreditCard, DollarSign, User, Save } from "lucide-react";
import Mo3tiButton from "../../layout/Mo3tiButton";
import StatusBadge from "../../table/StatusBadge";

const EmployeeDetailsModal = ({
  employee,
  onClose,
  currentTheme,
  t,
  language,
  theme,
  isRTL,
  showAlert,
}) => {
    const [formData, setFormData] = useState({
      employeeName: employee?.employeeName || "",
      employeeId: employee?.employeeId || "",
      email: employee?.personalInfo?.email || "",
      phone: employee?.personalInfo?.phone || "",
      emergencyPhone: employee?.personalInfo?.emergencyPhone || "",
      nationalId: employee?.personalInfo?.nationalId || "",
      dateOfBirth: employee?.personalInfo?.dateOfBirth || "",
      gender: employee?.personalInfo?.gender || "",
      maritalStatus: employee?.personalInfo?.maritalStatus || "",
      nationality: employee?.personalInfo?.nationality || "",
      address: employee?.personalInfo?.address || "",
      city: employee?.personalInfo?.city || "",
      country: employee?.personalInfo?.country || "",
      department: employee?.department || "",
      position: employee?.position || "",
      hiringDate: employee?.workInfo?.hiringDate || "",
      contractType: employee?.workInfo?.contractType || "",
      workingHours: employee?.workInfo?.workingHours || "",
      contractExpiration: employee?.workInfo?.contractExpiration || "",
      directManager: employee?.workInfo?.directManager || "",
      workLocation: employee?.workInfo?.workLocation || "",
      basicSalary: employee?.basicSalary || 0,
      bonus: employee?.bonus || 0,
      overtimeHours: employee?.overtimeHours || 0,
      overtimeTotal: employee?.overtimeTotal || 0,
      transportationAllowance: employee?.transportationAllowance || 0,
      mealAllowance: employee?.mealAllowance || 0,
      mobileAllowance: employee?.mobileAllowance || 0,
      bonusAdjustments: employee?.bonusAdjustments || 0,
      grossSalary: employee?.grossSalary || 0,
      taxMonth: employee?.taxMonth || 0,
      socialInsurance: employee?.socialInsurance || 0,
      penalty: employee?.penalty || 0,
      advancePayment: employee?.advancePayment || 0,
      totalDeductions: employee?.totalDeductions || 0,
      netSalary: employee?.netSalary || 0,
      kpiScore: employee?.kpiScore || 0,
      status: employee?.status || "Active",
      bankName: employee?.bankInfo?.bankName || "",
      accountNumber: employee?.bankInfo?.accountNumber || "",
      iban: employee?.bankInfo?.iban || "",
      branchCode: employee?.bankInfo?.branchCode || "",
    });

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleSave = async () => {
      setLoading(true);
      try {
        console.log("Saving employee data:", formData);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsEditing(false);
        showAlert(t("success"), t("employeeDataUpdatedSuccess"), "success");
      } catch (error) {
        console.error("Error saving employee data:", error);
        showAlert(t("error"), t("errorSavingEmployeeData"), "error");
      } finally {
        setLoading(false);
      }
    };

    const formStyles = {
      modal: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 2000,
        padding: "20px",
        overflowY: "auto",
      },
      modalContent: {
        background: currentTheme.colors.cardBackground,
        backdropFilter: "blur(25px)",
        border: `2px solid ${currentTheme.colors.border}`,
        borderRadius: "24px",
        padding: "40px",
        width: "100%",
        maxWidth: "1200px",
        margin: "20px auto",
        maxHeight: "90vh",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch", // للـ iOS
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
        //boxShadow: currentTheme.shadows.card,
        direction: isRTL ? "rtl" : "ltr",
      },
      header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "40px",
        paddingBottom: "20px",
        borderBottom: `2px solid ${currentTheme.colors.border}`,
      },
      title: {
        color: currentTheme.colors.textPrimary,
        fontSize: "32px",
        fontWeight: "800",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "16px",
      },
      closeButton: {
        background: "rgba(239, 68, 68, 0.1)",
        border: "2px solid rgba(239, 68, 68, 0.3)",
        borderRadius: "12px",
        padding: "12px",
        cursor: "pointer",
        color: "#ef4444",
        transition: "all 0.3s ease",
      },
      formContainer: {
        display: "grid",
        gap: "32px",
      },
      section: {
        background: currentTheme.colors.inputBackground,
        borderRadius: "20px",
        padding: "32px",
        border: `2px solid ${currentTheme.colors.border}`,
      },
      sectionTitle: {
        color: currentTheme.colors.textPrimary,
        fontSize: "24px",
        fontWeight: "700",
        marginBottom: "24px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        textTransform: "uppercase",
        letterSpacing: "1px",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: "24px",
      },
      formGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      },
      label: {
        color: currentTheme.colors.textSecondary,
        fontSize: "14px",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      },
      input: {
        background:
          theme === "dark"
            ? "rgba(30, 41, 59, 0.5)"
            : "rgba(255, 255, 255, 0.7)",
        border: `2px solid ${currentTheme.colors.border}`,
        borderRadius: "12px",
        padding: "16px",
        // color: currentTheme.colors.textPrimary,
        fontSize: "16px",
        fontWeight: "500",
        transition: "all 0.3s ease",
        outline: "none",
      },
      readOnlyInput: {
        background:
          theme === "dark"
            ? "rgba(30, 41, 59, 0.5)"
            : "rgba(255, 255, 255, 0.96)",
        border: `2px solid ${currentTheme.colors.borderLight}`,
        borderRadius: "12px",
        padding: "16px",
        color: currentTheme.colors.textMuted,
        fontSize: "16px",
        fontWeight: "500",
        cursor: "not-allowed",
      },
      actionButtons: {
        display: "flex",
        gap: "16px",
        justifyContent: "center",
        marginTop: "40px",
        paddingTop: "32px",
        borderTop: `2px solid ${currentTheme.colors.border}`,
      },
    };

    return (
      <div
        style={formStyles.modal}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div style={formStyles.modalContent}>
          <div style={formStyles.header}>
            <h1 style={formStyles.title}>
              <User size={32} />
              {t("employeeDetailsTitle")}
            </h1>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              {/* <button
              style={formStyles.closeButton}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)";
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <X size={24} />
            </button> */}
            </div>
          </div>

          <div style={formStyles.formContainer}>
            {/* Personal Information Section */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>
                <User size={24} />
                {t("personalInformation")}
              </h2>
              <div style={formStyles.formGrid}>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("fullName")}</label>
                  <input
                    type="text"
                    name="employeeName"
                    value={formData.employeeName}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("employeeId")}</label>
                  <input
                    type="text"
                    name="employeeId"
                    value={formData.employeeId}
                    style={formStyles.readOnlyInput}
                    readOnly
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("emailAddress")}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("phoneNumber")}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("emergencyPhone")}</label>
                  <input
                    type="tel"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("nationalId")}</label>
                  <input
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("dateOfBirth")}</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("gender")}</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    disabled={!isEditing}
                  >
                    <option value="">{t("selectGender")}</option>
                    <option value="Male">{t("male")}</option>
                    <option value="Female">{t("female")}</option>
                  </select>
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("maritalStatus")}</label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    disabled={!isEditing}
                  >
                    <option value="">{t("selectStatus")}</option>
                    <option value="Single">{t("single")}</option>
                    <option value="Married">{t("married")}</option>
                    <option value="Divorced">{t("divorced")}</option>
                    <option value="Widowed">{t("widowed")}</option>
                  </select>
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("nationality")}</label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("city")}</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("country")}</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
              </div>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t("address")}</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  style={
                    isEditing ? formStyles.input : formStyles.readOnlyInput
                  }
                  readOnly={!isEditing}
                />
              </div>
            </div>

            {/* Work Information Section */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>
                <Building size={24} />
                {t("workInformation")}
              </h2>
              <div style={formStyles.formGrid}>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("department")}</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("position")}</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("hiringDate")}</label>
                  <input
                    type="date"
                    name="hiringDate"
                    value={formData.hiringDate}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("contractType")}</label>
                  <select
                    name="contractType"
                    value={formData.contractType}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    disabled={!isEditing}
                  >
                    <option value="">{t("selectType")}</option>
                    <option value="Full-time">{t("fullTime")}</option>
                    <option value="Part-time">{t("partTime")}</option>
                    <option value="Contract">{t("contract")}</option>
                    <option value="Temporary">{t("temporary")}</option>
                  </select>
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("workingHours")}</label>
                  <input
                    type="text"
                    name="workingHours"
                    value={formData.workingHours}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("contractExpiration")}
                  </label>
                  <input
                    type="date"
                    name="contractExpiration"
                    value={formData.contractExpiration}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("directManager")}</label>
                  <input
                    type="text"
                    name="directManager"
                    value={formData.directManager}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("workLocation")}</label>
                  <input
                    type="text"
                    name="workLocation"
                    value={formData.workLocation}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("employmentStatus")}
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <StatusBadge
                      status={formData.status}
                      language={language}
                      t={t}
                    />
                    {isEditing && (
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        style={{ ...formStyles.input, flex: 1 }}
                      >
                        <option value="Active">{t("active")}</option>
                        <option value="Paid">{t("paid")}</option>
                        <option value="Pending">{t("pending")}</option>
                        <option value="Processing">{t("processing")}</option>
                        <option value="On Hold">{t("onHold")}</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Salary & Earnings Section */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>
                <DollarSign size={24} />
                {t("salaryAndEarnings")}
              </h2>
              <div style={formStyles.formGrid}>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("basicSalary")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="basicSalary"
                    value={formData.basicSalary}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("bonus")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="bonus"
                    value={formData.bonus}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t("overtimeHours")}</label>
                  <input
                    type="number"
                    name="overtimeHours"
                    value={formData.overtimeHours}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("overtimeTotal")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="overtimeTotal"
                    value={formData.overtimeTotal}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("transportationAllowance")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="transportationAllowance"
                    value={formData.transportationAllowance}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("mealAllowance")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="mealAllowance"
                    value={formData.mealAllowance}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("mobileAllowance")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="mobileAllowance"
                    value={formData.mobileAllowance}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("bonusAdjustments")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="bonusAdjustments"
                    value={formData.bonusAdjustments}
                    onChange={handleInputChange}
                    style={
                      isEditing ? formStyles.input : formStyles.readOnlyInput
                    }
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>
                    {t("grossSalary")} ({t("currency")})
                  </label>
                  <input
                    type="number"
                    name="grossSalary"
                    value={formData.grossSalary}
                    style={formStyles.readOnlyInput}
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Banking Information Section */}
            {/* Banking Information Section */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>
                <CreditCard size={24} />
                {t('bankingInformation')}
              </h2>
              <div style={formStyles.formGrid}>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('bankName')}</label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    style={isEditing ? formStyles.input : formStyles.readOnlyInput}
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('accountNumber')}</label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    style={isEditing ? formStyles.input : formStyles.readOnlyInput}
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('iban')}</label>
                  <input
                    type="text"
                    name="iban"
                    value={formData.iban}
                    onChange={handleInputChange}
                    style={isEditing ? formStyles.input : formStyles.readOnlyInput}
                    readOnly={!isEditing}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('branchCode')}</label>
                  <input
                    type="text"
                    name="branchCode"
                    value={formData.branchCode}
                    onChange={handleInputChange}
                    style={isEditing ? formStyles.input : formStyles.readOnlyInput}
                    readOnly={!isEditing}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={formStyles.actionButtons}>
              {isEditing && (
                <Mo3tiButton
                  variant="primary"
                  icon={Save}
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? t("saving") : t("saveChanges")}
                </Mo3tiButton>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

export default EmployeeDetailsModal;